<template>
  <div class="home">
    <b-overlay :show="isLoading" rounded="sm">
      <!-- In-call page -->
      <meet
          v-if="isJoinMeeting && isReadyToJoin"
          :appid="appid"
          :token="token"
          :channel="channel"
          :uid="Number(uid)"
          :userAccount="userAccount"
          :preMute="config?.mute"
          :preCameraOff="config?.cameraOff"
          @leave-meeting="handleLeaveMeeting"
      />

      <!-- Pre-join page -->
      <main-page
          v-else
          @join-meeting="handleJoinMeeting"
          :isJoining="isLoading"
          :errorMsg="errorMsg"
          :channel="channel"
          :appid="appid"
          :token="token"
      />
    </b-overlay>
  </div>
</template>

<script>
import {BOverlay} from "bootstrap-vue";
import axios from "axios";
import Meet from "@/components/Meet.vue";
import MainPage from "@/components/Main.vue";

const params = Object.fromEntries(new URLSearchParams(window.location.search).entries());

export default {
  name: "Home",
  components: {BOverlay, Meet, MainPage},
  data() {
    return {
      isLoading: true,
      isJoinMeeting: false,
      config: null,
      appid: null,
      channel: null,
      token: null,
      uid: null,                 // numeric uid (doctor)
      userAccount: null,         // optional display name
      orderData: {},
      order_uuid: null,
      errorMsg: null,
      expireTimeInSeconds: 0
    };
  },
  computed: {
    isReadyToJoin() {
      if (this.uid == null) return false;
      const n = Number(this.uid);
      return !!(this.appid && this.token && this.channel) && Number.isFinite(n) && n > 0;
    }
  },
  mounted() {
    this.order_uuid = this.$route.params.order_uuid;
    this.fetchOrderData(this.order_uuid);
  },
  watch: {
    isReadyToJoin(val) {
      if (!val && this.isJoinMeeting) {
        this.isJoinMeeting = false;
        this.$message?.warning?.(this.$t?.('meeting_became_unavailable') || 'تم تعطيل الانضمام للاجتماع بسبب فقدان البيانات.');
      }
    }
  },
  methods: {
    async fetchOrderData(order_token) {
      this.errorMsg = null;
      this.isLoading = true;
      try {
        const apiurl = window.api_url;
        const res = await axios.get(`${apiurl}/meeting/${order_token}`, {params});
        const data = res?.data?.result;
        console.log('Home-Received meeting data:', data);
        console.log('Full server response:', res?.data);
        
        // Validate payload before accessing fields
        if (!data || data.appid == null || data.channel == null || data.token == null || data.uid == null) {
          console.error('Invalid server data:', {
            hasData: !!data,
            hasAppid: data?.appid != null,
            hasChannel: data?.channel != null,
            hasToken: data?.token != null,
            hasUid: data?.uid != null,
            data: data
          });
          const serverMsg = res?.data?.msg || res?.data?.message;
          throw new Error(serverMsg || 'Invalid meeting data received from server - missing required fields');
        }
        
        // Additional token validation
        if (typeof data.token !== 'string' || data.token.trim().length === 0) {
          throw new Error('Invalid or empty token received from server');
        }
        
        // Validate token format (basic check for Agora token structure)
        console.log('[Home] Token details:', {
          length: data.token.length,
          startsWith: data.token.substring(0, 10),
          includes006: data.token.includes('006'),
          includes007: data.token.includes('007')
        });
        
        if (!data.token.includes('006') && !data.token.includes('007')) {
          console.warn('[Home] Token format may be invalid:', data.token.substring(0, 20) + '...');
        }

        // Validate UID first before setting anything
        const n = Number(data.uid);
        if (!Number.isFinite(n) || n <= 0) {
          console.error('[Home] Invalid UID:', data.uid, 'converted to:', n);
          throw new Error(`Invalid uid in meeting data: ${data.uid}`);
        }
        
        console.log('[Home] Setting meeting data:', {
          appid: data.appid,
          channel: data.channel,
          uid: n,
          tokenLength: data.token.length,
          userAccount: data.userAccount
        });

        // DO NOT send/store app certificate on the client.
        this.appid = data.appid;
        this.channel = data.channel;
        this.token = data.token;            // short-lived RTC token for this uid
        this.uid = n;                       // ensure numeric uid to match token's uid
        this.userAccount = data.userAccount || null;
        this.expireTimeInSeconds = data.expireTimeInSeconds || 0;
        this.orderData = data;
      } catch (e) {
        console.error('[Home] Error fetching meeting data:', {
          error: e,
          response: e.response?.data,
          status: e.response?.status,
          statusText: e.response?.statusText
        });
        
        this.errorMsg = e.response?.data?.msg || e.response?.msg || e.message || "Failed to load meeting";
        this.$message?.error?.(this.errorMsg);
        
        // Clear any partial data on error
        this.appid = null;
        this.channel = null;
        this.token = null;
        this.uid = null;
        this.userAccount = null;
        this.orderData = null;
      } finally {
        this.isLoading = false;
      }
    },
    async handleJoinMeeting(config) {
      this.config = {mute: !!config.mute, cameraOff: !!config.cameraOff};
      
      // Additional validation before joining
      if (!this.token || typeof this.token !== 'string' || this.token.trim().length === 0) {
        this.errorMsg = 'Invalid token - please refresh and try again';
        this.$message?.error?.(this.errorMsg);
        return;
      }
      
      if (!Number.isFinite(this.uid) || this.uid <= 0) {
        this.errorMsg = 'Invalid user ID - please refresh and try again';
        this.$message?.error?.(this.errorMsg);
        return;
      }
      
      if (this.isReadyToJoin) {
        console.log('[Home] Joining meeting with:', {
          appid: this.appid,
          channel: this.channel,
          uid: this.uid,
          tokenLength: this.token?.length,
          userAccount: this.userAccount
        });

        // Call join meeting API (without affecting the join process)
        try {
          const urlSearchParams = new URLSearchParams(window.location.search);
          const paramsLocal = Object.fromEntries(urlSearchParams.entries());
          let apiurl = window.api_url;
          
          console.log('[Home] Calling join meeting API:', {
            order_uuid: this.order_uuid,
            is_consultant: paramsLocal.is_consultant
          });

          const promise = axios
            .post(`${apiurl}/meeting/${this.order_uuid}/join?is_consultant=${paramsLocal.is_consultant}`);

          const response = await promise;
          console.log('[Home] Join meeting API response:', response.data);

        } catch (error) {
          console.error('[Home] Join meeting API error:', error);
          // Log the error but don't block the meeting join process
        }

        // Proceed with normal meeting join process
        this.isJoinMeeting = true;
      } else {
        this.isJoinMeeting = false;
        this.errorMsg = this.errorMsg || this.$t?.('meeting_not_ready') || 'بيانات الاجتماع غير جاهزة بعد. حاول مرة أخرى.';
        this.$message?.warning?.(this.errorMsg);
      }
    },
    async handleLeaveMeeting() {
      try {
        const apiurl = window.api_url;
        await axios.post(`${apiurl}/meeting/${this.order_uuid}/close`, {
          ...this.orderData,
          token: this.token
        });
      } catch (e) {
        console.log(e);
      }
      this.isJoinMeeting = false;
    }
  }
};
</script>